import { cn } from "../../lib/utils";

const SILHOUETTE_SIZE = 24;

// Dedicated neutral-placeholder stack for "hidden" identity mode. Renders N
// identical silhouettes (NO per-user email/label/title/alt/aria-per-user text,
// NO color variation, index-based React keys) plus an optional REAL "You"
// bubble for the viewer. Never reuse AvatarBubble/AvatarStack for the
// silhouettes — those emit per-user fingerprints (title/alt/initials/color).
//
// Props:
//   count    — number of hidden OTHER voters (EXCLUDES self). Callers must not
//              include the viewer here; self is rendered separately via
//              `selfUser`, else the viewer is double-counted (a "You" bubble +
//              a silhouette that is actually the viewer), inflating the total.
//   selfUser — when truthy, renders one real "You" bubble. Used only for its
//              presence; its identity fields are intentionally never surfaced.
export function AnonymousVoterPlaceholders({
  count = 0,
  selfUser = null,
  visibilityHint = null,
  max = 5,
  className,
}) {
  const total = Math.max(0, Number(count) || 0);
  const maxVisible = Math.max(0, Number(max) || 0);
  const visibleCount = Math.min(total, maxVisible);
  const overflow = Math.max(0, total - maxVisible);

  return (
    <div className={cn("flex flex-col gap-1", className)}>
      <div
        className="flex items-center -space-x-2"
        role="group"
        aria-label={`${total} hidden voter${total === 1 ? "" : "s"}`}
      >
        {selfUser ? <SelfBubble /> : null}
        {Array.from({ length: visibleCount }, (_, index) => (
          <Silhouette key={index} />
        ))}
        {overflow > 0 ? <OverflowChip value={overflow} /> : null}
      </div>
      {visibilityHint ? (
        <p
          data-testid="visibility-hint"
          className="text-xs text-slate-500 dark:text-slate-400"
        >
          {visibilityHint}
        </p>
      ) : null}
    </div>
  );
}

function Silhouette() {
  return (
    <div
      data-testid="hidden-silhouette"
      aria-hidden="true"
      className="flex items-center justify-center rounded-full border border-white bg-slate-200 text-[10px] font-semibold text-slate-500 shadow-sm dark:border-slate-900 dark:bg-slate-700 dark:text-slate-300"
      style={{ width: SILHOUETTE_SIZE, height: SILHOUETTE_SIZE }}
    >
      ?
    </div>
  );
}

// Self is always rendered as a REAL, distinct bubble (R10). It is labelled
// "You" and intentionally does NOT surface selfUser's email/displayName, so no
// identifier leaks even in hidden mode.
function SelfBubble() {
  return (
    <div
      title="You"
      className="flex items-center justify-center rounded-full border border-white bg-slate-100 text-[10px] font-semibold text-slate-700 shadow-sm dark:border-slate-900 dark:bg-slate-600 dark:text-slate-100"
      style={{ width: SILHOUETTE_SIZE, height: SILHOUETTE_SIZE }}
    >
      You
    </div>
  );
}

function OverflowChip({ value }) {
  return (
    <div
      aria-hidden="true"
      className="flex items-center justify-center rounded-full border border-white bg-slate-200 text-[10px] font-semibold text-slate-700 shadow-sm dark:border-slate-900 dark:bg-slate-700 dark:text-slate-200"
      style={{ width: SILHOUETTE_SIZE, height: SILHOUETTE_SIZE }}
    >
      +{value}
    </div>
  );
}
